import { LitElement, html, svg, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { linearScale, bandScale, ticks, plotBox } from '../lib/scales.js';
import { patternDefs } from '../lib/patterns.js';
import { hours as formatHours } from '../lib/format.js';

/**
 * The frame every chart in this app draws inside: one value axis (PRD 7.3 — never two), a
 * category axis, a recessive grid, the pattern defs the confidence fills reference, and a plot
 * box handed to the view.
 *
 * A view sets `categories` (the weeks, usually), `max` (the top of the value axis), and a
 * `marks` function. The frame measures itself, builds the scales, and calls
 * `marks({ x, y, plot, svg })` — `svg` is Lit's SVG tag, so marks are declarative and the frame
 * stays the only place that knows about margins, ticks, and resize.
 *
 * Grid and axes are deliberately quiet: hairline gridlines in --grid, ticks in --tick-ink, and
 * no chart junk. The marks are what the reader should see.
 */
class ChartFrame extends LitElement {
  static properties = {
    categories: { type: Array },
    max: { type: Number },
    min: { type: Number },
    height: { type: Number },
    marks: { attribute: false },
    valueLabel: { type: String, attribute: 'value-label' },
    categoryLabel: { type: String, attribute: 'category-label' },
    formatValue: { attribute: false },
    formatCategory: { attribute: false },
    bandPadding: { type: Number, attribute: 'band-padding' },
    tickCount: { type: Number, attribute: 'tick-count' },
    rule: { type: Number }, // an optional reference line on the value axis (capacity, target)
    ruleLabel: { type: String, attribute: 'rule-label' },
    _width: { state: true },
  };

  constructor() {
    super();
    this.categories = [];
    this.max = 0;
    this.min = 0;
    this.height = 260;
    this.marks = null;
    this.valueLabel = 'Hours';
    this.categoryLabel = '';
    this.formatValue = (v) => formatHours(v, { unit: false });
    this.formatCategory = (c) => String(c);
    this.bandPadding = 0.25;
    this.tickCount = 5;
    this.rule = null;
    this.ruleLabel = '';
    this._width = 0;
  }

  static styles = css`
    :host {
      display: block;
    }
    svg {
      display: block;
      width: 100%;
      height: auto;
      overflow: visible;
    }
    .tick text {
      fill: var(--tick-ink);
      font-size: 11px;
      font-family: var(--font-sans);
    }
    .axis-label {
      fill: var(--ink-muted);
      font-size: 10px;
      font-family: var(--font-eyebrow);
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }
    .grid line {
      stroke: var(--grid);
      stroke-width: 1;
      shape-rendering: crispEdges;
    }
    .axis line {
      stroke: var(--axis);
      stroke-width: 1;
      shape-rendering: crispEdges;
    }
    .rule line {
      stroke: var(--ink-strong);
      stroke-width: 2;
      stroke-dasharray: 5 3;
    }
    .rule text {
      fill: var(--ink-strong);
      font-size: 11px;
      font-weight: 600;
      font-family: var(--font-sans);
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // Charts live in a filter-driven layout, so width changes without the window changing.
    this._observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (width !== this._width) this._width = width;
    });
    this._observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
  }

  /** The geometry of the current render, for a view that needs to place something itself. */
  get geometry() {
    const width = this._width || 640;
    const axis = ticks([this.min ?? 0, this.max ?? 0], { count: this.tickCount });
    const plot = plotBox({
      width,
      height: this.height,
      margin: { left: 56, right: 16, top: 16, bottom: this.categoryLabel ? 44 : 30 },
    });
    const y = linearScale({ domain: axis.domain, range: [plot.top + plot.height, plot.top] });
    const x = bandScale({
      domain: this.categories ?? [],
      range: [plot.left, plot.left + plot.width],
      padding: this.bandPadding,
    });
    return { width, plot, x, y, axis };
  }

  render() {
    const { width, plot, x, y, axis } = this.geometry;
    // Show every label when they fit, then every other, then every fourth: an axis that overlaps
    // its own ticks is worse than one that shows fewer.
    const step = Math.max(1, Math.ceil((this.categories.length * 46) / Math.max(1, plot.width)));

    return html`
      <svg
        viewBox="0 0 ${width} ${this.height}"
        role="img"
        aria-label=${`${this.valueLabel} by ${this.categoryLabel || 'category'}`}
      >
        ${unsafeSVG(patternDefs())}

        <g class="grid">
          ${axis.values.map(
            (value) => svg`<line x1=${plot.left} x2=${plot.left + plot.width}
                                 y1=${y(value)} y2=${y(value)} />`,
          )}
        </g>

        <g class="tick">
          ${axis.values.map(
            (value) => svg`<text x=${plot.left - 8} y=${y(value)} dy="0.32em"
                                 text-anchor="end">${this.formatValue(value)}</text>`,
          )}
        </g>

        <g class="axis">
          <line
            x1=${plot.left}
            x2=${plot.left + plot.width}
            y1=${plot.top + plot.height}
            y2=${plot.top + plot.height}
          />
        </g>

        ${this.marks ? this.marks({ x, y, plot, svg }) : ''}
        ${
          this.rule !== null && this.rule !== undefined && Number.isFinite(this.rule)
            ? svg`<g class="rule">
                  <line x1=${plot.left} x2=${plot.left + plot.width}
                        y1=${y(this.rule)} y2=${y(this.rule)} />
                  ${
                    this.ruleLabel
                      ? svg`<text x=${plot.left + plot.width} y=${y(this.rule) - 6}
                                  text-anchor="end">${this.ruleLabel}</text>`
                      : ''
                  }
                </g>`
            : ''
        }

        <g class="tick">
          ${(this.categories ?? []).map((category, i) =>
            i % step === 0
              ? svg`<text x=${x.centre(category)} y=${plot.top + plot.height + 16}
                          text-anchor="middle">${this.formatCategory(category, i)}</text>`
              : '',
          )}
        </g>

        <text
          class="axis-label"
          transform=${`translate(12 ${plot.top + plot.height / 2}) rotate(-90)`}
          text-anchor="middle"
        >
          ${this.valueLabel}
        </text>
        ${
          this.categoryLabel
            ? svg`<text class="axis-label" x=${plot.left + plot.width / 2}
                      y=${this.height - 6} text-anchor="middle">${this.categoryLabel}</text>`
            : ''
        }
      </svg>
    `;
  }
}

customElements.define('chart-frame', ChartFrame);
